import type { Permission } from '../domain/constants.js';
import type { Feature } from './plans.js';
import type { SectorProfile } from './sectors.js';

/**
 * RELACIÓN DE OBJETIVOS
 *
 * Un "objetivo" es un resultado que la plataforma se compromete a entregar al
 * cliente. No es un menú ni un permiso: es una promesa concreta, con su estado
 * y su motivo. Cada objetivo declara de qué depende:
 *
 *   feature   → la licencia SaaS contratada (plan)
 *   sectors   → la licencia comercial aportada (actividades autorizadas)
 *   permission→ el rol del usuario
 *   setup     → configuración que la empresa aún no ha completado
 *   license   → exige licencia comercial vigente (no caducada)
 *
 * Capacidad efectiva = Plan ∩ Licencia comercial ∩ Rol ∩ Configuración.
 */

export const OBJECTIVE_MODULES = [
  'empresa',
  'relaciones',
  'ventas',
  'compras',
  'operaciones',
  'finanzas',
  'documentos',
  'cumplimiento',
  'inteligencia',
  'plataforma',
] as const;
export type ObjectiveModule = (typeof OBJECTIVE_MODULES)[number];

export const MODULE_LABELS: Record<ObjectiveModule, string> = {
  empresa: 'Company & Structure',
  relaciones: 'Clients & Suppliers',
  ventas: 'Sales',
  compras: 'Purchasing',
  operaciones: 'Operations',
  finanzas: 'Finance',
  documentos: 'Documents & Traceability',
  cumplimiento: 'Compliance',
  inteligencia: 'Artificial Intelligence',
  plataforma: 'Platform',
};

/** Requisitos de configuración que un objetivo puede exigir. */
export const SETUP_KEYS = [
  'company.created',
  'company.license_uploaded',
  'company.bank_account',
  'company.tax_number',
  'company.numbering',
  'company.template',
  'catalog.any_item',
  'counterparty.any',
] as const;
export type SetupKey = (typeof SETUP_KEYS)[number];

export const SETUP_LABELS: Record<SetupKey, string> = {
  'company.created': 'Create your first company',
  'company.license_uploaded': 'Upload the trade license',
  'company.bank_account': 'Register a bank account',
  'company.tax_number': 'Register the tax number / TRN',
  'company.numbering': 'Define document numbering',
  'company.template': 'Choose a document template',
  'catalog.any_item': 'Add a product or service to the catalog',
  'counterparty.any': 'Add a client or supplier',
};

export interface Objective {
  id: string;
  module: ObjectiveModule;
  /** La promesa, escrita desde el lado del cliente. */
  title: string;
  detail: string;
  feature: Feature;
  /** `'*'` = aplica a cualquier actividad. */
  sectors: SectorProfile[] | '*';
  permission: Permission;
  setup?: SetupKey[];
  /** Exige licencia comercial vigente para poder operar. */
  requiresValidLicense?: boolean;
  /** Métrica viva asociada, si la hay (se resuelve en el backend). */
  metric?: {
    key: string;
    label: string;
    /** 'lower_better' → el objetivo es reducirla (p. ej. facturas vencidas). */
    direction: 'higher_better' | 'lower_better' | 'neutral';
    target?: number;
  };
  /** Ruta del producto donde se cumple el objetivo. */
  route?: string;
}

export const OBJECTIVES: Objective[] = [
  /* ── Empresa ─────────────────────────────────────────────────────── */
  {
    id: 'company.onboarding',
    module: 'empresa',
    title: 'Onboard a company by reading its license',
    detail: 'The AI extracts legal name, license number, authority, activities and expiry. You just confirm.',
    feature: 'core',
    sectors: '*',
    permission: 'company.manage',
    route: '/empresas/nueva',
  },
  {
    id: 'company.multi',
    module: 'empresa',
    title: 'Run multiple companies in a single workspace',
    detail: 'Persistent switcher. Switching company changes logo, license, banks, currency, taxes and numbering.',
    feature: 'multi_company',
    sectors: '*',
    permission: 'company.view',
    metric: { key: 'companies.count', label: 'Active companies', direction: 'neutral' },
    route: '/empresas',
  },
  {
    id: 'company.people',
    module: 'empresa',
    title: 'Register partners, UBOs, directors and signatories',
    detail: 'With ownership, signing power, identity documents and expiries.',
    feature: 'core',
    sectors: '*',
    permission: 'company.manage',
    setup: ['company.created'],
    route: '/empresas/personas',
  },
  {
    id: 'numbering.series',
    module: 'empresa',
    title: 'Own numbering per company and document type',
    detail: 'Series independientes, prefijos, reinicio anual. Sin huecos ni duplicados.',
    feature: 'core',
    sectors: '*',
    permission: 'settings.manage',
    setup: ['company.created'],
    route: '/administracion/numeracion',
  },
  {
    id: 'tax.engine',
    module: 'empresa',
    title: 'Motor de impuestos configurable',
    detail: 'VAT, zero-rated, exento, reverse charge y retenciones, con el texto legal obligatorio en el documento.',
    feature: 'core',
    sectors: '*',
    permission: 'settings.manage',
    setup: ['company.tax_number'],
    route: '/administracion/impuestos',
  },
  {
    id: 'banks.accounts',
    module: 'empresa',
    title: 'Bank accounts per company and per deal',
    detail: 'IBAN, SWIFT, correspondent and instructions, inserted automatically into every invoice.',
    feature: 'core',
    sectors: '*',
    permission: 'bank.view',
    setup: ['company.bank_account'],
    route: '/administracion/bancos',
  },

  /* ── Relaciones ──────────────────────────────────────────────────── */
  {
    id: 'crm.counterparties',
    module: 'relaciones',
    title: 'Single record for client, supplier or counterparty',
    detail: 'Historial completo: operaciones, facturas, pagos, documentos, riesgo y estado KYC en una sola pantalla.',
    feature: 'core',
    sectors: '*',
    permission: 'company.view',
    metric: { key: 'counterparties.count', label: 'Contrapartes registradas', direction: 'higher_better' },
    route: '/relaciones',
  },
  {
    id: 'crm.shared',
    module: 'relaciones',
    title: 'Compartir contrapartes entre empresas del grupo',
    detail: 'A client is created once, and you decide which companies can see it.',
    feature: 'multi_company',
    sectors: '*',
    permission: 'company.view',
    route: '/relaciones',
  },
  {
    id: 'crm.credit',
    module: 'relaciones',
    title: 'Credit limit and overdue alerts',
    detail: 'The platform warns before issuing to a client that exceeds its limit or has overdue balances.',
    feature: 'crm',
    sectors: '*',
    permission: 'report.view',
    metric: { key: 'counterparties.overdue', label: 'Clientes con vencidos', direction: 'lower_better', target: 0 },
    route: '/relaciones',
  },

  /* ── Ventas ──────────────────────────────────────────────────────── */
  {
    id: 'quote.issue',
    module: 'ventas',
    title: 'Issue quotations and proposals',
    detail: 'Scope, deliverables, exclusions, milestones, validity and terms. Bilingual if needed.',
    feature: 'core',
    sectors: '*',
    permission: 'document.create',
    setup: ['company.created'],
    requiresValidLicense: true,
    metric: { key: 'quotes.month', label: 'Cotizaciones este mes', direction: 'higher_better' },
    route: '/ventas/cotizaciones',
  },
  {
    id: 'quote.conversion',
    module: 'ventas',
    title: 'Turn an accepted quotation into a contract or invoice',
    detail: 'Sin reescribir un solo dato. La trazabilidad queda unida para siempre.',
    feature: 'core',
    sectors: '*',
    permission: 'document.create',
    metric: { key: 'quotes.conversion_rate', label: 'Conversion rate', direction: 'higher_better', target: 40 },
    route: '/ventas/cotizaciones',
  },
  {
    id: 'contract.issue',
    module: 'ventas',
    title: 'Redactar y firmar contratos',
    detail: 'From a quotation, template, natural language or clause library.',
    feature: 'contracts',
    sectors: '*',
    permission: 'document.create',
    requiresValidLicense: true,
    metric: { key: 'contracts.active', label: 'Contratos vigentes', direction: 'neutral' },
    route: '/ventas/contratos',
  },
  {
    id: 'contract.clauses',
    module: 'ventas',
    title: 'Clause library with multiple versions',
    detail: 'Favorable al proveedor, equilibrada o favorable al cliente. Breve o completa. ES / EN / AR.',
    feature: 'clause_library',
    sectors: '*',
    permission: 'document.create',
    route: '/ventas/contratos/clausulas',
  },
  {
    id: 'contract.obligations',
    module: 'ventas',
    title: 'Automatic calendar of contract obligations',
    detail: 'The AI extracts start, renewal, notice, milestones, penalties and warranties, and turns them into alerts.',
    feature: 'ai_advanced',
    sectors: '*',
    permission: 'document.create',
    metric: { key: 'obligations.upcoming', label: 'Obligations in 30 days', direction: 'neutral' },
    route: '/ventas/contratos',
  },
  {
    id: 'invoice.issue',
    module: 'ventas',
    title: 'Issue compliant invoices',
    detail: 'Tax Invoice, proforma, advance, final, credit note. With QR, bank details and correct tax wording.',
    feature: 'core',
    sectors: '*',
    permission: 'document.issue_fiscal',
    setup: ['company.created'],
    requiresValidLicense: true,
    metric: { key: 'invoices.month', label: 'Facturas este mes', direction: 'neutral' },
    route: '/ventas/facturas',
  },
  {
    id: 'invoice.recurring',
    module: 'ventas',
    title: 'Recurring invoicing and subscriptions',
    detail: 'Retainers, mantenimientos y licencias que se emiten y avisan solos.',
    feature: 'recurring',
    sectors: ['software', 'consulting', 'general'],
    permission: 'document.create',
    route: '/ventas/recurrentes',
  },
  {
    id: 'invoice.multicurrency',
    module: 'ventas',
    title: 'Facturar en cualquier moneda',
    detail: 'Separate company and document currencies, dated exchange rate and consolidation in reports.',
    feature: 'multicurrency',
    sectors: '*',
    permission: 'document.create',
    route: '/ventas/facturas',
  },

  /* ── Compras ─────────────────────────────────────────────────────── */
  {
    id: 'purchase.flow',
    module: 'compras',
    title: 'Ciclo de compra completo',
    detail: 'Request -> RFQ -> comparison -> order -> receipt -> invoice -> approval -> payment.',
    feature: 'purchasing',
    sectors: '*',
    permission: 'document.create',
    route: '/compras',
  },
  {
    id: 'purchase.three_way',
    module: 'compras',
    title: 'Automatic three-way matching',
    detail: 'Order, receipt and invoice compared. The platform flags quantity, price, tax or duplicate mismatches.',
    feature: 'purchasing',
    sectors: '*',
    permission: 'document.approve',
    metric: { key: 'purchase.mismatches', label: 'Discrepancias abiertas', direction: 'lower_better', target: 0 },
    route: '/compras/conciliacion',
  },
  {
    id: 'expense.ticket',
    module: 'compras',
    title: 'Fotografiar un ticket y que se convierta en gasto',
    detail: 'OCR of supplier, date, net, tax, total and category. The original is never overwritten.',
    feature: 'ocr',
    sectors: '*',
    permission: 'document.create',
    metric: { key: 'expenses.month', label: 'Gastos capturados este mes', direction: 'neutral' },
    route: '/compras/gastos',
  },
  {
    id: 'expense.reimbursable',
    module: 'compras',
    title: 'Gastos reembolsables por cliente o empleado',
    detail: 'Marcar un gasto como repercutible y verlo aparecer en la siguiente factura del proyecto.',
    feature: 'projects',
    sectors: ['consulting', 'software', 'general'],
    permission: 'document.create',
    route: '/compras/gastos',
  },

  /* ── Operaciones ─────────────────────────────────────────────────── */
  {
    id: 'project.milestones',
    module: 'operaciones',
    title: 'Proyectos, hitos y entregables',
    detail: 'El contrato firmado crea el proyecto. El hito aprobado genera la factura.',
    feature: 'projects',
    sectors: ['software', 'consulting'],
    permission: 'document.create',
    metric: { key: 'milestones.billable', label: 'Hitos facturables', direction: 'lower_better', target: 0 },
    route: '/operaciones/proyectos',
  },
  {
    id: 'project.timesheet',
    module: 'operaciones',
    title: 'Horas facturables y coste interno',
    detail: 'Tarifa por empleado y actividad, importe a facturar y margen real.',
    feature: 'projects',
    sectors: ['software', 'consulting'],
    permission: 'document.create',
    route: '/operaciones/horas',
  },
  {
    id: 'loi.issue',
    module: 'operaciones',
    title: 'LOI, ICPO, SCO y FCO en minutos',
    detail: 'Buy-side and sell-side with product, specification, quantity, incoterm, port, inspection and payment terms.',
    feature: 'intl_trade',
    sectors: ['trading', 'brokerage'],
    permission: 'document.create',
    requiresValidLicense: true,
    route: '/operaciones/loi',
  },
  {
    id: 'dealroom',
    module: 'operaciones',
    title: 'Private Deal Room per transaction',
    detail: 'Comprador, vendedor, brokers, bancos, documentos, etapas y aprobaciones en una sala cerrada.',
    feature: 'dealroom',
    sectors: ['trading', 'brokerage'],
    permission: 'company.view',
    metric: { key: 'deals.open', label: 'Operaciones abiertas', direction: 'neutral' },
    route: '/operaciones/deals',
  },
  {
    id: 'brokerage.commission',
    module: 'operaciones',
    title: 'Protect the commission: NCNDA, IMFPA and introduction evidence',
    detail: 'Who introduced whom, when and with which document. The commission is no longer disputed.',
    feature: 'dealroom',
    sectors: ['brokerage'],
    permission: 'document.create',
    route: '/operaciones/deals',
  },
  {
    id: 'logistics.shipping',
    module: 'operaciones',
    title: 'Incoterms, ports, inspection and shipping documents',
    detail: 'Packing list, B/L, certificate of origin and SGS inspection linked to the deal.',
    feature: 'intl_trade',
    sectors: ['trading'],
    permission: 'document.create',
    route: '/operaciones/logistica',
  },

  /* ── Finanzas ────────────────────────────────────────────────────── */
  {
    id: 'payment.register',
    module: 'finanzas',
    title: 'Record incoming and outgoing payments',
    detail: 'Parciales, anticipos, pagos superiores, saldos a favor y transferencias sin identificar.',
    feature: 'core',
    sectors: '*',
    permission: 'payment.register',
    metric: { key: 'payments.unallocated', label: 'Pagos sin asignar', direction: 'lower_better', target: 0 },
    route: '/finanzas/pagos',
  },
  {
    id: 'payment.allocation',
    module: 'finanzas',
    title: 'One payment for several invoices - and vice versa',
    detail: 'Partial allocation, reassignable, without deleting history.',
    feature: 'core',
    sectors: '*',
    permission: 'payment.reassign',
    route: '/finanzas/pagos',
  },
  {
    id: 'receipt.issue',
    module: 'finanzas',
    title: 'Automatic receipt on payment',
    detail: 'Each payment closes its link: invoice -> payment -> receipt.',
    feature: 'core',
    sectors: '*',
    permission: 'payment.register',
    route: '/finanzas/recibos',
  },
  {
    id: 'invoice.collection',
    module: 'finanzas',
    title: 'Cero facturas vencidas sin reclamar',
    detail: 'Automatic reminders and balance aging per client.',
    feature: 'core',
    sectors: '*',
    permission: 'report.view',
    metric: { key: 'invoices.overdue', label: 'Facturas vencidas', direction: 'lower_better', target: 0 },
    route: '/finanzas/cobros',
  },
  {
    id: 'reconciliation.bank',
    module: 'finanzas',
    title: 'Assisted bank reconciliation',
    detail: 'Importar movimientos y aceptar la coincidencia que propone la IA. Comisiones y diferencias de cambio incluidas.',
    feature: 'ai_advanced',
    sectors: '*',
    permission: 'payment.register',
    route: '/finanzas/conciliacion',
  },
  {
    id: 'treasury.forecast',
    module: 'finanzas',
    title: 'Know how much cash you will have in 90 days',
    detail: 'Expected inflows, expected outflows, recurring items, taxes and future shortfall.',
    feature: 'reports',
    sectors: '*',
    permission: 'report.view',
    route: '/finanzas/tesoreria',
  },
  {
    id: 'profitability',
    module: 'finanzas',
    title: 'Real margin of each deal',
    detail: 'Contratado, facturado, cobrado, costes, gastos, comisiones, horas e impuestos, hasta el margen final.',
    feature: 'reports',
    sectors: '*',
    permission: 'report.view',
    route: '/finanzas/rentabilidad',
  },
  {
    id: 'approvals.rules',
    module: 'finanzas',
    title: 'Approval rules by amount',
    detail: 'Up to 5,000 automatic, up to 25,000 manager, up to 100,000 director, above that dual approval.',
    feature: 'approvals',
    sectors: '*',
    permission: 'document.approve',
    metric: { key: 'approvals.pending', label: 'Aprobaciones pendientes', direction: 'lower_better', target: 0 },
    route: '/administracion/aprobaciones',
  },

  /* ── Documentos y trazabilidad ───────────────────────────────────── */
  {
    id: 'traceability.chain',
    module: 'documentos',
    title: 'Todo cobro o pago tiene expediente',
    detail: 'Quotation -> contract -> project -> invoice -> payment -> receipt. Every link clickable, none orphaned.',
    feature: 'core',
    sectors: '*',
    permission: 'company.view',
    metric: { key: 'documents.orphan', label: 'Documentos sin expediente', direction: 'lower_better', target: 0 },
    route: '/documentos',
  },
  {
    id: 'correction.superuser',
    module: 'documentos',
    title: 'Correct a closed document without breaking history',
    detail: 'An issued document has no "Edit": it has "Correct". With reason, level, previous version and impact.',
    feature: 'core',
    sectors: '*',
    permission: 'document.correct',
    route: '/documentos',
  },
  {
    id: 'docs.vault',
    module: 'documentos',
    title: 'Document management with OCR, versions and expiries',
    detail: 'Original intact, processed version and structured record. With a log of who downloaded what.',
    feature: 'ocr',
    sectors: '*',
    permission: 'document.download',
    route: '/documentos',
  },
  {
    id: 'dossier.export',
    module: 'documentos',
    title: 'Generate the full dossier of a deal',
    detail: 'Cover, summary, parties, quotation, contract, annexes, invoices, payments, receipts and history.',
    feature: 'core',
    sectors: '*',
    permission: 'document.download',
    route: '/documentos',
  },
  {
    id: 'audit.trail',
    module: 'documentos',
    title: 'Complete and immutable audit trail',
    detail: 'Who created, edited, approved, linked, downloaded or printed. Nothing is deleted, everything is logged.',
    feature: 'core',
    sectors: '*',
    permission: 'audit.view',
    route: '/administracion/auditoria',
  },
  {
    id: 'portal.client',
    module: 'documentos',
    title: 'Portal para clientes y proveedores',
    detail: 'Aceptar cotizaciones, firmar, descargar facturas, pagar, subir comprobantes y actualizar KYC.',
    feature: 'portal',
    sectors: '*',
    permission: 'document.send',
    route: '/administracion/portal',
  },
  {
    id: 'signature.electronic',
    module: 'documentos',
    title: 'Electronic signature with evidence',
    detail: 'Signatory, date, IP and sealed document, attached to the record.',
    feature: 'esignature',
    sectors: '*',
    permission: 'document.sign',
    route: '/documentos',
  },

  /* ── Cumplimiento ────────────────────────────────────────────────── */
  {
    id: 'license.registry',
    module: 'cumplimiento',
    title: 'Licencia comercial siempre vigente',
    detail: 'Number, authority, authorized activities and expiry, extracted from the document and confirmed by you.',
    feature: 'core',
    sectors: '*',
    permission: 'company.manage',
    setup: ['company.license_uploaded'],
    metric: { key: 'license.days_to_expiry', label: 'Days to expiry', direction: 'higher_better', target: 90 },
    route: '/cumplimiento/licencias',
  },
  {
    id: 'expiry.alerts',
    module: 'cumplimiento',
    title: 'No document expires by surprise',
    detail: 'Alerts at 120, 90, 60, 30, 15 and 7 days via dashboard, email, mobile, WhatsApp and calendar.',
    feature: 'expiries',
    sectors: '*',
    permission: 'company.view',
    metric: { key: 'documents.expiring_60', label: 'Documents expiring in 60 days', direction: 'lower_better', target: 0 },
    route: '/cumplimiento/vencimientos',
  },
  {
    id: 'kyc.dossier',
    module: 'cumplimiento',
    title: 'Expediente KYC / AML por contraparte',
    detail: 'License, MOA, shareholders, UBO, passports, source of funds, restricted countries and decision owner.',
    feature: 'kyc',
    sectors: '*',
    permission: 'kyc.view',
    metric: { key: 'kyc.pending', label: 'KYC pendientes', direction: 'lower_better', target: 0 },
    route: '/cumplimiento/kyc',
  },
  {
    id: 'period.close',
    module: 'cumplimiento',
    title: 'Cerrar mes, trimestre y ejercicio',
    detail: 'Un documento de periodo cerrado exige reapertura, ajuste o rectificativa. Nunca un cambio silencioso.',
    feature: 'core',
    sectors: '*',
    permission: 'period.reopen',
    route: '/administracion/periodos',
  },

  /* ── Inteligencia artificial ─────────────────────────────────────── */
  {
    id: 'ai.command',
    module: 'inteligencia',
    title: 'Work by talking, not navigating',
    detail: '"Invoice ABC 12,500 AED for June consulting, payment in 15 days." The AI prepares the document; you confirm.',
    feature: 'ai_basic',
    sectors: '*',
    permission: 'ai.use',
    metric: { key: 'ai.credits_used', label: 'AI credits used', direction: 'neutral' },
    route: '/ia',
  },
  {
    id: 'ai.describe',
    module: 'inteligencia',
    title: 'Descriptions, scopes and clauses written by the AI',
    detail: 'Introduction, scope, phases, deliverables, exclusions and terms. With styling and translation.',
    feature: 'ai_basic',
    sectors: '*',
    permission: 'ai.use',
    route: '/ia',
  },
  {
    id: 'ai.extract',
    module: 'inteligencia',
    title: 'Leer un documento y rellenar los campos',
    detail: 'Licenses, passports, visas, invoices and receipts, with per-field confidence and human confirmation.',
    feature: 'ocr',
    sectors: '*',
    permission: 'ai.use',
    route: '/ia',
  },
  {
    id: 'ai.review',
    module: 'inteligencia',
    title: 'Detectar lo que no cuadra antes de firmar',
    detail: 'Different amounts between quotation and contract, incompatible dates, contradictory clauses, missing fields.',
    feature: 'ai_advanced',
    sectors: '*',
    permission: 'ai.use',
    route: '/ia',
  },
  {
    id: 'ai.risk',
    module: 'inteligencia',
    title: 'Alertas de riesgo y fraude',
    detail: 'Factura duplicada, importe anormal, banco modificado, proveedor nuevo con pago elevado, fraccionamiento.',
    feature: 'ai_advanced',
    sectors: '*',
    permission: 'report.view',
    metric: { key: 'risk.open', label: 'Alertas de riesgo abiertas', direction: 'lower_better', target: 0 },
    route: '/ia/riesgos',
  },
  {
    id: 'ai.guardrails',
    module: 'inteligencia',
    title: 'La IA nunca cambia un importe en silencio',
    detail: 'Price, dates, banks, percentages, jurisdiction, taxes and parties require explicit confirmation with a diff view.',
    feature: 'ai_basic',
    sectors: '*',
    permission: 'ai.use',
    route: '/ia',
  },

  /* ── Plataforma ──────────────────────────────────────────────────── */
  {
    id: 'automation.rules',
    module: 'plataforma',
    title: 'Automatizaciones sin programar',
    detail: 'Accepted quotation -> contract. Approved milestone -> invoice. Payment received -> receipt. License near expiry -> task.',
    feature: 'automations',
    sectors: '*',
    permission: 'settings.manage',
    route: '/administracion/automatizaciones',
  },
  {
    id: 'migration.import',
    module: 'plataforma',
    title: 'Traer sus datos actuales',
    detail: 'Excel, CSV o ZIP. La IA propone el mapeo de columnas y usted lo aprueba.',
    feature: 'migration',
    sectors: '*',
    permission: 'settings.manage',
    route: '/administracion/migracion',
  },
  {
    id: 'mobile.app',
    module: 'plataforma',
    title: 'Work from mobile',
    detail: 'Fotografiar ticket, aprobar pago, firmar, consultar factura, registrar cobro y dictar notas de voz.',
    feature: 'mobile',
    sectors: '*',
    permission: 'company.view',
    route: '/administracion/movil',
  },
  {
    id: 'api.public',
    module: 'plataforma',
    title: 'API y webhooks',
    detail: 'Connect accounting, bank, e-commerce or ERP against the same traceability.',
    feature: 'api',
    sectors: '*',
    permission: 'settings.manage',
    route: '/administracion/api',
  },
  {
    id: 'security.advanced',
    module: 'plataforma',
    title: 'MFA, IP restriction and device control',
    detail: 'Expiring links, download protection and access logging.',
    feature: 'security_advanced',
    sectors: '*',
    permission: 'settings.manage',
    route: '/administracion/seguridad',
  },
  {
    id: 'sso',
    module: 'plataforma',
    title: 'Corporate single sign-on (SSO)',
    detail: 'SAML u OIDC contra su directorio.',
    feature: 'sso',
    sectors: '*',
    permission: 'settings.manage',
    route: '/administracion/seguridad',
  },
  {
    id: 'whitelabel',
    module: 'plataforma',
    title: 'Su marca, su dominio',
    detail: 'Colores, logotipo, plantillas y dominio propio en documentos y portal.',
    feature: 'whitelabel',
    sectors: '*',
    permission: 'settings.manage',
    route: '/administracion/marca',
  },
];

export const OBJECTIVE_INDEX: Record<string, Objective> = Object.fromEntries(
  OBJECTIVES.map((o) => [o.id, o]),
);

export function objectivesByModule(module: ObjectiveModule): Objective[] {
  return OBJECTIVES.filter((o) => o.module === module);
}
